import { ReactNode, useEffect, useRef, useState } from 'react'
import { CloseWithoutSavingDialog } from './CloseWithoutSavingDialog'

type CloseReason = 'escapeKeyDown' | 'backdropClick'

type ConfirmedClose = {
  /** Closes at once, or asks first when the setting form holds changes that closing would lose. */
  requestClose: (event?: unknown, reason?: CloseReason) => void
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void
  closeConfirmDialog: ReactNode
}

/** A field in the middle of an edit reports it to the form on blur, which pressing Escape does not cause. */
function flushPendingFieldEdit(): HTMLElement | null {
  const field = document.activeElement instanceof HTMLElement ? document.activeElement : null
  field?.blur()
  return field
}

export function useConfirmedClose(onClose: () => void): ConfirmedClose {
  const hasUnsavedChanges = useRef(false)
  const pendingClose = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [onUnsavedChangesChange] = useState(() => (changed: boolean): void => {
    hasUnsavedChanges.current = changed
  })

  const closeUnlessChanged = (fieldToReturnFocusTo?: HTMLElement | null): void => {
    if (!hasUnsavedChanges.current) {
      onClose()
      return
    }
    fieldToReturnFocusTo?.focus()
    setConfirmOpen(true)
  }
  const closeOnceFieldEditIsReported = (): void => {
    if (pendingClose.current) {
      return
    }
    const field = flushPendingFieldEdit()
    pendingClose.current = setTimeout(() => {
      pendingClose.current = null
      closeUnlessChanged(field)
    }, 0)
  }

  useEffect(
    () => () => {
      if (pendingClose.current) {
        clearTimeout(pendingClose.current)
        pendingClose.current = null
      }
    },
    []
  )

  return {
    requestClose: (_event, reason) =>
      reason === 'escapeKeyDown' ? closeOnceFieldEditIsReported() : closeUnlessChanged(),
    onUnsavedChangesChange,
    closeConfirmDialog: (
      <CloseWithoutSavingDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          onClose()
        }}
      />
    ),
  }
}
